import { Component, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { NgForm } from "@angular/forms";

import { TranslateService } from "@ngx-translate/core";
import { MessageService as PrimeMessageService } from 'primeng/api';
import { TokenService } from '@app/services/net-remit/token.service';
import { UsersService } from '@app/services/net-remit/users.service';
import { MessageService } from '@app/core/message.service';
import { AuthenticationService } from '@app/core/authentication/authentication.service';
import { SharedService } from '@app/services/net-remit/shared-functions.service';

import { UserTokenValidationResult, UserTokenGenerationReasons } from '@app/shared/models/TokenModels';
import { routesConstants } from '@app/shared/models/LinksView';

import { ResetPassContext } from '@app/shared';

import { delay, finalize, takeWhile } from 'rxjs/operators';
import { of } from "rxjs";

@Component({
    selector: 'app-passforgot',
    templateUrl: './passforgot.component.html',
    styleUrls: ['./passforgot.component.scss'],
    standalone: false
})

export class PassforgotComponent implements OnInit, OnDestroy {

  alive: boolean = true;
  existExplicitError: boolean;
  isError: boolean;
  isLoading: boolean= true;
  isThirdPersonReset: boolean; // Cuando el reseteo lo realiza un administrador para un cliente con problemas de reseteo
  messagesError: Array<any>;
  modalOption: NgbModalOptions = { backdrop: 'static', keyboard: false };
  passwordError: Object;
  resetPassInfo: ResetPassContext = new ResetPassContext();
  secondPasswordError: Object;
  success: boolean;
  tokenValidationResult: UserTokenValidationResult;

  private modalRef: NgbModalRef;

  @ViewChild('modalforgot') private modalforgot: any;
  @ViewChild('passForgotForm') passForgotForm: NgForm;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private tokenService: TokenService,
    private userService: UsersService,
    private messageService: MessageService,
    private sharedService: SharedService,
    private modalService: NgbModal,
    private authService: AuthenticationService,
    private primeMessageService: PrimeMessageService,
    private translate: TranslateService
  ) {

    // sacamos el token que nos llega como parametro en la ruta.
    this.route.params.pipe(takeWhile(() => this.alive)).subscribe(params => this.resetPassInfo.token = params.userToken );
    this.initErrorMessages();
  }

  ngOnInit() {
    if (this.resetPassInfo.token && this.resetPassInfo.token.trim().length > 0) {
      this.validateToken();
    }
  }

  /**
    * Comprueba que el token recibido es válido
    * Recibe un objeto tokenUserInfo
    */
  private validateToken() {
    this.isError = false;
    // solicitamos la comprobación del token
    this.tokenService.validateToken(this.resetPassInfo.token).pipe(takeWhile(() => this.alive))
      .subscribe({ next: (result) => {
        this.tokenValidationResult = result;
        this.resetPassInfo.username = result.userName;
        this.isThirdPersonReset = this.authService.credentials ? true : false;
        this.isLoading = false;
        if (this.tokenValidationResult.action === UserTokenGenerationReasons.ChangePassword) {
          // Es un cambio de contraseña de un usuario ya auntenticado, por lo que redirigimos al login
          this.router.navigate([routesConstants.landing], { queryParams: { passwordUpdated: true }, replaceUrl: true});
        } else {
          this.startAndConfigureModal();
        }
      }, error: () => {
        this.isError = true;
        this.messagesError = this.sharedService.getErrorFormMessagesList(this.messageService.get(0).message);
        this.isLoading = false;
      }});
  }

  private startAndConfigureModal() {
    this.modalRef = this.modalService.open(this.modalforgot, this.modalOption);
    this.modalRef.result.then(() => {
        this.closedModal();
      }, () => {
        this.closedModal();
      });
  }

  private closedModal() {
    if(this.isThirdPersonReset){
      this.router.navigate([routesConstants.administrationUsers.administrationUsers + '/' + routesConstants.administrationUsers.update], { replaceUrl: true });
    } else {
      this.router.navigate([routesConstants.landing], { replaceUrl: true });
    }
  }

  public sendPassword() {
    this.isLoading = true;
    this.success = false;
    this.isError = false;
    this.initErrorMessages();
    this.userService.resetPassword(this.resetPassInfo)
      .pipe(finalize(() => {
        this.passForgotForm.form.markAsPristine();
        this.isLoading = false;
      })
      ).pipe(takeWhile(() => this.alive)).subscribe({
        next: () => {
          this.success = true;
          this.messagesError = undefined;
          this.primeMessageService.add({
            severity: 'success',
            summary: this.translate.instant('Modal.Success'),
            detail: this.translate.instant("Change Pass.Done Redirecting"),
            key: 'password-forgot-message'
          });
          const source = of(true).pipe(delay(1800)).pipe(takeWhile(() => this.alive)).subscribe(result => {
            this.modalRef.close('OK');
          });
        },
        error: (error) => {
          this.existExplicitError = false;
          this.isError = true;
          this.messagesError = this.sharedService.getErrorFormMessagesList(this.messageService.get(0).message || error.error || error.message);
          this.messagesError.forEach(currentError => {
            if(this[currentError.field] != undefined){
              this.existExplicitError = true;
              this[currentError.field].description = currentError.description;
              this[currentError.field].quantityMax = currentError.quantityMax;
              this[currentError.field].quantityMin = currentError.quantityMin;
            }
          });
          if(!this.existExplicitError){
            this.primeMessageService.add({
              key: 'password-forgot-message',
              sticky: true,
              severity:'error',
              summary: this.translate.instant('Modal.Error'),
              detail: this.translate.instant('User.Errors.Reset Password')
            });
          }
        }}
      );
  }

  private initErrorMessages(){
    this.passwordError = new Object();
    this.secondPasswordError = new Object();
  }

  ngOnDestroy() {
    this.alive = false;
  }
}
